import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/feature/PageHero';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const companyJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${siteUrl}/company`,
    url: `${siteUrl}/company`,
    name: '会社概要｜創業50年の精密ネジ製造｜おかやまネジ製作所',
    description: '岡山市おかやまネジ製作所の会社概要。代表取締役：岡山太郎。1976年創業、従業員5名。岡山市北区で精密ネジ製造・特注ネジ製作・難削材加工を行う町工場。',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'トップ', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '会社概要', item: `${siteUrl}/company` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${siteUrl}/#organization`,
    name: 'おかやまネジ製作所',
    url: siteUrl,
    foundingDate: '1976',
    description: '岡山市の精密ネジ製造業者。創業50年の技術で特注ネジ・チタン加工・1本から対応。',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '北区○○町1-2-3',
      addressLocality: '岡山市',
      addressRegion: '岡山県',
      postalCode: '700-0000',
      addressCountry: 'JP',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+81-86-012-3456',
      faxNumber: '+81-86-012-3457',
      contactType: 'customer service',
      availableLanguage: 'Japanese',
      hoursAvailable: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '09:00',
        closes: '18:00',
      },
    },
    numberOfEmployees: { '@type': 'QuantitativeValue', value: 5 },
    member: {
      '@type': 'Person',
      name: '岡山 太郎',
      jobTitle: '代表取締役',
    },
    memberOf: [
      { '@type': 'Organization', name: '岡山県金属工業協同組合' },
      { '@type': 'Organization', name: '岡山商工会議所' },
    ],
  },
];

const CompanyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = '会社概要｜創業50年の精密ネジ製造｜おかやまネジ製作所';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '岡山市おかやまネジ製作所の会社概要。代表取締役：岡山太郎。1976年創業、従業員5名。岡山市北区で精密ネジ製造・特注ネジ製作・難削材加工を行う町工場。');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${siteUrl}/company`);

    const scriptId = 'company-jsonld';
    if (document.getElementById(scriptId)) return;
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(companyJsonLd);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  const companyInfo = [
    { label: '会社名', value: 'おかやまネジ製作所' },
    { label: '代表取締役', value: '岡山 太郎' },
    { label: '所在地', value: '〒700-0000 岡山県岡山市北区○○町1-2-3' },
    { label: '電話番号', value: '086-012-3456' },
    { label: 'FAX番号', value: '086-012-3457' },
    { label: '設立', value: '1976年（昭和51年）' },
    { label: '従業員数', value: '5名' },
    { label: '営業時間', value: '9:00 〜 18:00' },
    { label: '定休日', value: '日曜日・祝日' },
    { label: '事業内容', value: '精密ネジ製造、特注ネジ製作、難削材加工' },
    { label: '加入団体', value: '岡山県金属工業協同組合、岡山商工会議所' },
  ];

  const history = [
    { year: '1976年', event: '岡山市北区にて創業。汎用旋盤による精密ネジ製造を開始' },
    { year: '1985年', event: 'NC旋盤を導入。生産能力の向上と品質の安定化を実現' },
    { year: '1995年', event: 'チタン・ステンレスなど難削材加工の技術を確立' },
    { year: '2005年', event: '最新CNC旋盤を導入。0.01mm単位の超精密加工を実現' },
    { year: '2010年', event: '三次元測定機を導入。品質管理体制を強化' },
    { year: '2015年', event: '航空宇宙分野への納入実績を開始' },
    { year: '2020年', event: '医療機器分野への参入。超小型精密ネジの製造開始' },
    { year: '2025年', event: '創業50周年を迎える。さらなる技術革新を目指す' },
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <PageHero
          title="会社概要"
          breadcrumbs={[
            { label: 'トップ', path: '/' },
            { label: '会社概要' },
          ]}
          imageUrl="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/ade13bbbcea5f91fc8d9f8759c0b1182.jpeg"
        />

        {/* 会社情報 */}
        <section className="py-16 sm:py-20 lg:py-28 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-navy mb-12 sm:mb-16">
              会社情報
            </h2>

            <div className="bg-white border-2 border-navy rounded-xl overflow-hidden">
              {companyInfo.map((info, index) => (
                <div
                  key={index}
                  className={`grid grid-cols-1 sm:grid-cols-3 ${
                    index !== companyInfo.length - 1 ? 'border-b border-navy' : ''
                  }`}
                >
                  <div className="bg-navy/5 px-6 py-4 sm:py-6 font-bold text-navy text-sm sm:text-base">
                    {info.label}
                  </div>
                  <div className="sm:col-span-2 px-6 py-4 sm:py-6 text-gray-700 text-sm sm:text-base">
                    {info.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 代表挨拶 */}
        <section className="py-16 sm:py-20 lg:py-28 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              <div className="lg:col-span-4">
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/c6b96623f6b882627811eeb1c915b76a.webp"
                    alt="代表取締役 岡山太郎"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="lg:col-span-8">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-navy mb-6 sm:mb-8">
                  代表挨拶
                </h2>
                <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-gray-600 leading-relaxed">
                  <p>
                    おかやまネジ製作所のホームページをご覧いただき、誠にありがとうございます。代表取締役の岡山太郎と申します。
                  </p>
                  <p>
                    当社は1976年の創業以来、岡山市を拠点に精密ネジ製造一筋で歩んでまいりました。創業50年を迎えた今も、「お客様の困ったを解決する」という創業時の理念を大切にしています。
                  </p>
                  <p>
                    規格品では対応できない特殊な形状のネジ、チタンやステンレスなどの難削材加工、1本からの小ロット対応。これらは、大手メーカーでは対応が難しい分野です。しかし、私たちのような町工場だからこそ、お客様一人ひとりのニーズに寄り添い、柔軟に対応することができます。
                  </p>
                  <p>
                    最新のCNC旋盤と、熟練職人の技術。この両輪があるからこそ、0.01mm単位の超精密加工を実現できています。機械だけでは出せない品質、人の手だけでは実現できない精度。その両方を追求し続けることが、私たちの強みです。
                  </p>
                  <p>
                    岡山市を中心とした近隣市町村の製造業の皆様に、高品質な製品と迅速なサービスを提供し続けることで、地域産業の発展に貢献してまいります。
                  </p>
                  <p>
                    新製品の試作開発でお困りの設計担当者様、既存の規格品では対応できずお悩みの皆様、ぜひ一度ご相談ください。50年の経験と技術で、必ずお役に立てると確信しております。
                  </p>
                  <p className="text-right font-bold text-navy text-xl sm:text-2xl mt-8 sm:mt-12">
                    代表取締役　岡山 太郎
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 沿革 */}
        <section className="py-16 sm:py-20 lg:py-28 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-navy text-center mb-12 sm:mb-16 lg:mb-20">
              沿革
            </h2>

            <div className="relative">
              <div className="absolute left-4 sm:left-8 top-0 bottom-0 w-0.5 bg-navy"></div>

              <div className="space-y-8 sm:space-y-12">
                {history.map((item, index) => (
                  <div key={index} className="relative pl-12 sm:pl-20">
                    <div className="absolute left-0 sm:left-4 w-8 h-8 sm:w-12 sm:h-12 flex items-center justify-center bg-navy rounded-full">
                      <div className="w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-full"></div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 sm:p-6 hover:shadow-lg transition-shadow">
                      <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-navy mb-2 sm:mb-3">
                        {item.year}
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                        {item.event}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* アクセス */}
        <section className="py-16 sm:py-20 lg:py-28 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-navy text-center mb-12 sm:mb-16">
              アクセス
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              <div className="rounded-2xl overflow-hidden shadow-xl h-96 lg:h-auto">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d26234.01234567890!2d133.9350!3d34.6617!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDM5JzQyLjEiTiAxMzPCsDU2JzA2LjAiRQ!5e0!3m2!1sja!2sjp!4v1234567890123!5m2!1sja!2sjp"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="おかやまネジ製作所の地図"
                ></iframe>
              </div>
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-navy mb-6 sm:mb-8">
                  アクセス情報
                </h3>
                <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-gray-600">
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-2xl text-navy mt-1 flex-shrink-0"></i>
                    <div className="ml-4">
                      <p className="font-bold text-navy mb-1">所在地</p>
                      <p>〒700-0000<br />岡山県岡山市北区○○町1-2-3</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-phone-line text-2xl text-navy mt-1 flex-shrink-0"></i>
                    <div className="ml-4">
                      <p className="font-bold text-navy mb-1">電話番号</p>
                      <p>086-012-3456</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-printer-line text-2xl text-navy mt-1 flex-shrink-0"></i>
                    <div className="ml-4">
                      <p className="font-bold text-navy mb-1">FAX番号</p>
                      <p>086-012-3457</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-time-line text-2xl text-navy mt-1 flex-shrink-0"></i>
                    <div className="ml-4">
                      <p className="font-bold text-navy mb-1">営業時間</p>
                      <p>9:00 〜 18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-calendar-line text-2xl text-navy mt-1 flex-shrink-0"></i>
                    <div className="ml-4">
                      <p className="font-bold text-navy mb-1">定休日</p>
                      <p>日曜日・祝日</p>
                    </div>
                  </div>
                </div>
                <a
                  href="tel:086-012-3456"
                  className="mt-8 inline-block w-full sm:w-auto bg-navy text-white px-8 py-4 rounded-lg text-center font-bold hover:bg-navy-light transition-colors whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  お電話でのお問い合わせ
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CompanyPage;